import _ from "lodash";
import {
  bulkCreateStandardRubricCriteriaMutation,
  createStandardRubricDescriptorMutation,
  bulkCreateStandardRubricDescriptorsMutation,
  removeStandardRubricCriteriasMutation,
  removeStandardRubricDescriptorsMutation,
  updateStandardRubricCriteriasMutation,
  updateStandardRubricDescriptorsMutation,
} from "./StandardRubricMutations";
import client from "lib/apolloClient/client";
import { updateScoreToolDataMutation } from "modules/AssessmentTool/AssessmentToolMutations";
import { generateRandomId } from "Utils";
import { getIsGradeScaleValueSelectionAllowed } from "./Utils";
import {
  getAssessmentToolNodeDetailsFromCacheV2,
  writeAssessmentToolNodeDetailsInCache,
} from "modules/CommonGraphqlHelpers";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { STANDARD_BASED_RUBRIC } from "Constants/stringConstants";
import { getAssessmentToolNodeDetails } from "modules/AssessmentTool/AssessmentToolModule";

export const bulkCreateStandardRubricCriteria = ({
  input,
  type,
  skipPutAfterItemId = false,
  shouldFetchToolNodeDetails = true,
}) => {
  return async dispatch => {
    const standardRubric = getAssessmentToolNodeDetailsFromCacheV2({
      id: _.isArray(input) ? _.first(input).toolId : input.toolId,
      type,
    });

    try {
      const standardRubricCriterias = _.get(
        standardRubric,
        "standardRubricCriterias",
        []
      );

      // Convert input to array if it's not already
      const inputArray = _.castArray(input);
      const newCriteria = [];

      const isGradeScaleSelectionAllowed = getIsGradeScaleValueSelectionAllowed(
        type
      );

      // Process each input item
      _.forEach(inputArray, inputItem => {
        let { putAfterItemId } = inputItem;

        // case 1 - if there is no standardRubricCriterias then putAfterItemId = null which means it'll be inserted at the beginning
        // case 2 - user clicks "Add criterion" then putAfterItemId = undefined which means it'll be inserted at the end
        if (_.isUndefined(putAfterItemId)) {
          putAfterItemId = _.get(_.last(standardRubricCriterias), "id", null);
        }

        const putAfterItemIndex = _.findIndex(standardRubricCriterias, {
          id: putAfterItemId,
        });

        const newCriterion = {
          id: generateRandomId(),
          label: _.get(inputItem, "label", null),
          description: _.get(inputItem, "description", null),
          displaySequence: _.get(
            inputItem,
            "displaySequence",
            putAfterItemIndex + 1
          ),
          standard: null,
          benchmark: null,
          descriptors: _.map(inputItem?.descriptors, descriptor => {
            return {
              id: generateRandomId(),
              score: descriptor?.score || null,
              gradeScaleValue: null,
              standardGradeScaleValueDescriptor: null,
              ...descriptor,
              __typename: "StandardRubricDescriptor",
            };
          }),
          __typename: "StandardRubricCriteria",
        };

        // Calculate maxScore based on descriptors' scores
        const descriptorScores = _.map(newCriterion.descriptors, "score");
        newCriterion.maxScore = _.max(descriptorScores) || null;

        newCriteria.push({ ...newCriterion, putAfterItemId });
      });

      const _bulkCreateStandardRubricCriteriaResponse = {
        response: _.map(newCriteria, criterion =>
          _.omit(criterion, "putAfterItemId")
        ),
        warning: null,
        isSuccess: true,
        __typename: "BulkCreateStandardRubricCriteriaResponse",
      };

      const toolId = _.isArray(input) ? _.first(input).toolId : input.toolId;

      const response = await client.mutate({
        mutation: bulkCreateStandardRubricCriteriaMutation,
        variables: {
          input: _.map(inputArray, item => {
            return {
              ...item,
              putAfterItemId:
                type === STANDARD_BASED_RUBRIC || skipPutAfterItemId
                  ? undefined
                  : item.putAfterItemId === undefined
                  ? _.get(_.last(standardRubricCriterias), "id", null)
                  : item.putAfterItemId,
              totalAutoGenerateDescriptors: isGradeScaleSelectionAllowed
                ? 5
                : undefined,
            };
          }),
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            bulkCreateStandardRubricCriteria: _bulkCreateStandardRubricCriteriaResponse,
            __typename: "PlannerMutations",
          },
        },

        update: (
          cache,
          {
            data: {
              planner: { bulkCreateStandardRubricCriteria },
            },
          }
        ) => {
          if (bulkCreateStandardRubricCriteria?.isSuccess) {
            const newCriteriaFromResponse = _.get(
              bulkCreateStandardRubricCriteria,
              "response",
              []
            );

            let updatedCriterias = [...standardRubricCriterias];

            // Insert each new criterion at the appropriate position
            _.forEach(newCriteria, (criterion, idx) => {
              const putAfterItemId = criterion.putAfterItemId;
              const putAfterItemIndex = _.findIndex(updatedCriterias, {
                id: putAfterItemId,
              });

              if (putAfterItemIndex === -1) {
                // If putAfterItemId not found or null
                if (
                  criterion?.displaySequence &&
                  criterion.displaySequence > 0
                ) {
                  // Use displaySequence to determine insertion position
                  const insertPosition = Math.min(
                    criterion.displaySequence - 1,
                    updatedCriterias.length
                  );
                  updatedCriterias = [
                    ..._.slice(updatedCriterias, 0, insertPosition),
                    newCriteriaFromResponse[idx],
                    ..._.slice(updatedCriterias, insertPosition),
                  ];
                } else {
                  // If no valid displaySequence, insert at beginning
                  updatedCriterias = [
                    newCriteriaFromResponse[idx],
                    ...updatedCriterias,
                  ];
                }
              } else {
                // Insert after the specified item
                updatedCriterias = [
                  ..._.slice(updatedCriterias, 0, putAfterItemIndex + 1),
                  newCriteriaFromResponse[idx],
                  ..._.slice(updatedCriterias, putAfterItemIndex + 1),
                ];
              }
            });

            // Update display sequences for all criteria
            updatedCriterias = _.map(updatedCriterias, (criteria, index) => ({
              ...criteria,
              displaySequence: index + 1,
            }));

            // Sort criteria by displaySequence
            updatedCriterias = _.sortBy(updatedCriterias, "displaySequence");

            // Calculate total max score for associateScoreTool
            const totalMaxScore = _.reduce(
              updatedCriterias,
              (sum, criteria) => {
                return sum + (criteria.maxScore || 0);
              },
              0
            );

            // Update the associateScoreTool maxScore
            const updatedAssociateScoreTool = {
              ..._.get(standardRubric, "associateScoreTool", {}),
              scoreToolData: {
                ..._.get(
                  standardRubric,
                  "associateScoreTool.scoreToolData",
                  {}
                ),
                maxScore: totalMaxScore,
              },
            };

            const data = {
              ...standardRubric,
              standardRubricCriterias: updatedCriterias,
              associateScoreTool: _.has(
                standardRubric,
                "associateScoreTool.scoreToolData"
              )
                ? updatedAssociateScoreTool
                : null,
            };

            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: toolId,
                data,
                type,
              })
            );
          }
        },
      });

      // gradeScale inside StandardRubric is derived from StandardRubricDescriptor
      // When we createAssessmentTool then there is no rows (criteria) hence gradeScale is null
      // once rows are created then fetching the tool again to have gradeScale in the cache
      // which will further fetch gradeScaleValues
      if (_.isEmpty(standardRubricCriterias) && shouldFetchToolNodeDetails) {
        dispatch(
          getAssessmentToolNodeDetails({
            id: _.isArray(input) ? _.first(input).toolId : input.toolId,
            type: "STANDARD_RUBRIC",
          })
        );
      }

      return _.get(
        response,
        "data.planner.bulkCreateStandardRubricCriteria.response"
      );
    } catch (e) {
      writeAssessmentToolNodeDetailsInCache({
        id: Array.isArray(input) ? input[0].toolId : input.toolId,
        data: standardRubric,
        type,
      });
    }
  };
};

export const createStandardRubricDescriptor = ({
  input,
  toolId,
  type,
  gradeScaleValue = null,
  standardGradeScaleValueDescriptor = null,
}) => {
  return async () => {
    const standardRubric = getAssessmentToolNodeDetailsFromCacheV2({
      id: toolId,
      type,
    });

    try {
      const standardRubricCriterias = _.get(
        standardRubric,
        "standardRubricCriterias",
        []
      );

      let putAfterItemIndex = _.findIndex(standardRubricCriterias, {
        id: input?.putAfterItemId,
      });

      putAfterItemIndex = putAfterItemIndex === -1 ? 0 : putAfterItemIndex;

      const _createStandardRubricDescriptorResponse = {
        response: {
          id: generateRandomId(),
          label: _.get(input, "label", ""),
          score: _.get(input, "score", null),
          description: _.get(input, "description", ""),
          displaySequence: putAfterItemIndex + 1,
          gradeScaleValue,
          standardGradeScaleValueDescriptor,
          __typename: "StandardRubricDescriptor",
        },
        warning: null,
        isSuccess: true,
        __typename: "CreateStandardRubricDescriptorResponse",
      };

      const response = await client.mutate({
        mutation: createStandardRubricDescriptorMutation,
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            createStandardRubricDescriptor: _createStandardRubricDescriptorResponse,
            __typename: "PlannerMutations",
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { createStandardRubricDescriptor },
            },
          }
        ) => {
          if (createStandardRubricDescriptor?.isSuccess) {
            const updatedCriterias = _.map(
              standardRubricCriterias,
              criterion => {
                if (criterion.id === input.criteriaId) {
                  // Add the new descriptor to this criteria
                  let updatedDescriptors;
                  const descriptorToAdd =
                    createStandardRubricDescriptor.response;

                  // If putAfterItemId is null, insert at index 0
                  if (_.isNull(input.putAfterItemId)) {
                    updatedDescriptors = [
                      descriptorToAdd,
                      ...criterion.descriptors,
                    ];
                  }
                  // If putAfterItemId is undefined, append to end
                  else if (_.isUndefined(input.putAfterItemId)) {
                    updatedDescriptors = [
                      ...criterion.descriptors,
                      descriptorToAdd,
                    ];
                  }
                  // If putAfterItemId has a value, insert after that item
                  else {
                    const descriptorIndex = _.findIndex(criterion.descriptors, {
                      id: input.putAfterItemId,
                    });
                    if (descriptorIndex !== -1) {
                      updatedDescriptors = [
                        ..._.slice(
                          criterion.descriptors,
                          0,
                          descriptorIndex + 1
                        ),
                        descriptorToAdd,
                        ..._.slice(criterion.descriptors, descriptorIndex + 1),
                      ];
                    } else {
                      // If putAfterItemId not found, append to end
                      updatedDescriptors = [
                        ...criterion.descriptors,
                        descriptorToAdd,
                      ];
                    }
                  }

                  // Recalculate display sequences
                  updatedDescriptors = _.map(
                    updatedDescriptors,
                    (descriptor, index) => {
                      return {
                        ...descriptor,
                        displaySequence: index + 1,
                      };
                    }
                  );

                  // Calculate new max score if the new descriptor has a score
                  const descriptorScores = _.map(updatedDescriptors, "score");
                  const maxScore = _.max(descriptorScores) || null;

                  return {
                    ...criterion,
                    descriptors: updatedDescriptors,
                    maxScore,
                  };
                }

                return criterion;
              }
            );

            // Calculate total max score for associateScoreTool
            const totalMaxScore = _.reduce(
              updatedCriterias,
              (sum, criteria) => {
                return sum + (criteria.maxScore || 0);
              },
              0
            );

            // Update the associateScoreTool maxScore
            const updatedAssociateScoreTool = {
              ..._.get(standardRubric, "associateScoreTool", {}),
              scoreToolData: {
                ..._.get(
                  standardRubric,
                  "associateScoreTool.scoreToolData",
                  {}
                ),
                maxScore: totalMaxScore,
              },
            };

            const data = {
              ...standardRubric,
              standardRubricCriterias: updatedCriterias,
              associateScoreTool: _.has(
                standardRubric,
                "associateScoreTool.scoreToolData"
              )
                ? updatedAssociateScoreTool
                : null,
            };

            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: toolId,
                data,
                type,
              })
            );
          }
        },
      });

      return _.get(
        response,
        "data.planner.createStandardRubricDescriptor.response"
      );
    } catch (e) {
      writeAssessmentToolNodeDetailsInCache({
        id: toolId,
        data: standardRubric,
        type,
      });
    }
  };
};

export const bulkCreateStandardRubricDescriptors = params => {
  const { input, toolId, type } = params;

  return async () => {
    const standardRubric = getAssessmentToolNodeDetailsFromCacheV2({
      id: toolId,
      type,
    });

    try {
      const standardRubricCriterias = _.get(
        standardRubric,
        "standardRubricCriterias",
        []
      );

      // Convert input to array if it's not already
      const inputArray = _.castArray(input);
      const newDescriptors = [];

      // Process each input item
      _.forEach(inputArray, inputItem => {
        let { putAfterItemId } = inputItem;

        // Find the criteria this descriptor belongs to
        const criteriaIndex = _.findIndex(standardRubricCriterias, {
          id: inputItem.criteriaId,
        });

        if (criteriaIndex !== -1) {
          const criteria = standardRubricCriterias[criteriaIndex];
          const existingDescriptors = criteria.descriptors || [];

          // If putAfterItemId is undefined, append to end of descriptors
          if (_.isUndefined(putAfterItemId)) {
            putAfterItemId = _.get(_.last(existingDescriptors), "id", null);
          }

          const putAfterItemIndex = _.findIndex(existingDescriptors, {
            id: putAfterItemId,
          });

          const newDescriptor = {
            id: generateRandomId(),
            label: _.get(inputItem, "label", ""),
            score: _.get(inputItem, "score", null),
            description: _.get(inputItem, "description", null),
            displaySequence: _.get(
              inputItem,
              "displaySequence",
              putAfterItemIndex + 1
            ),
            gradeScaleValue: null,
            standardGradeScaleValueDescriptor: null,
            __typename: "StandardRubricDescriptor",
          };

          newDescriptors.push({
            ...newDescriptor,
            criteriaId: inputItem.criteriaId,
            putAfterItemId,
          });
        }
      });

      const _bulkCreateStandardRubricDescriptorsResponse = {
        response: _.map(newDescriptors, descriptor =>
          _.omit(descriptor, ["criteriaId", "putAfterItemId"])
        ),
        warning: null,
        isSuccess: true,
        __typename: "BulkCreateStandardRubricDescriptorsResponse",
      };

      const response = await client.mutate({
        mutation: bulkCreateStandardRubricDescriptorsMutation,
        variables: {
          input: inputArray,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            bulkCreateStandardRubricDescriptors: _bulkCreateStandardRubricDescriptorsResponse,
            __typename: "PlannerMutations",
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { bulkCreateStandardRubricDescriptors },
            },
          }
        ) => {
          if (bulkCreateStandardRubricDescriptors?.isSuccess) {
            const newDescriptorsFromResponse = _.get(
              bulkCreateStandardRubricDescriptors,
              "response",
              []
            );

            // Group new descriptors by criteriaId
            const descriptorsByCriteria = _.groupBy(
              newDescriptors,
              "criteriaId"
            );

            const updatedCriterias = _.map(
              standardRubricCriterias,
              criterion => {
                const descriptorsToAdd = descriptorsByCriteria[criterion.id];

                if (descriptorsToAdd) {
                  let updatedDescriptors = [...criterion.descriptors];

                  // Insert each new descriptor at the appropriate position
                  _.forEach(descriptorsToAdd, descriptor => {
                    const putAfterItemId = descriptor.putAfterItemId;
                    const putAfterItemIndex = _.findIndex(updatedDescriptors, {
                      id: putAfterItemId,
                    });

                    const descriptorToInsert = _.find(
                      newDescriptorsFromResponse,
                      d => d.id === descriptor.id
                    );

                    if (!descriptorToInsert) {
                      return; // Skip if descriptor not found in response
                    }

                    if (putAfterItemIndex === -1) {
                      // If putAfterItemId not found or null, insert at beginning
                      if (
                        descriptor?.displaySequence &&
                        descriptor.displaySequence > 0
                      ) {
                        // Use displaySequence to determine insertion position
                        const insertPosition = Math.min(
                          descriptor.displaySequence - 1,
                          updatedDescriptors.length
                        );
                        updatedDescriptors.splice(
                          insertPosition,
                          0,
                          descriptorToInsert
                        );
                      } else {
                        // If no valid displaySequence, insert at beginning
                        updatedDescriptors.unshift(descriptorToInsert);
                      }
                    } else {
                      // Insert after the specified item
                      updatedDescriptors.splice(
                        putAfterItemIndex + 1,
                        0,
                        descriptorToInsert
                      );
                    }
                  });

                  // Update display sequences for all descriptors
                  updatedDescriptors = _.map(
                    updatedDescriptors,
                    (descriptor, index) => ({
                      ...descriptor,
                      displaySequence: index + 1,
                    })
                  );

                  // Sort descriptors by displaySequence
                  updatedDescriptors = _.sortBy(
                    updatedDescriptors,
                    "displaySequence"
                  );

                  // Calculate new max score
                  const descriptorScores = _.map(updatedDescriptors, "score");
                  const maxScore = _.max(descriptorScores) || null;

                  return {
                    ...criterion,
                    descriptors: updatedDescriptors,
                    maxScore,
                  };
                }

                return criterion;
              }
            );

            // Calculate total max score for associateScoreTool
            const totalMaxScore = _.reduce(
              updatedCriterias,
              (sum, criteria) => {
                return sum + (criteria.maxScore || 0);
              },
              0
            );

            // Update the associateScoreTool maxScore
            const updatedAssociateScoreTool = {
              ..._.get(standardRubric, "associateScoreTool", {}),
              scoreToolData: {
                ..._.get(
                  standardRubric,
                  "associateScoreTool.scoreToolData",
                  {}
                ),
                maxScore: totalMaxScore,
              },
            };

            const data = {
              ...standardRubric,
              standardRubricCriterias: updatedCriterias,
              associateScoreTool: _.has(
                standardRubric,
                "associateScoreTool.scoreToolData"
              )
                ? updatedAssociateScoreTool
                : null,
            };

            writeAssessmentToolNodeDetailsInCache({
              id: toolId,
              data,
              type,
            });
          }
        },
      });

      return _.get(
        response,
        "data.planner.bulkCreateStandardRubricDescriptors.response"
      );
    } catch (e) {
      console.error(e);
      writeAssessmentToolNodeDetailsInCache({
        id: toolId,
        data: standardRubric,
        type,
      });
    }
  };
};

export const removeStandardRubricCriterias = ({ toolId, ids, type }) => {
  return async () => {
    const standardRubric = getAssessmentToolNodeDetailsFromCacheV2({
      id: toolId,
      type,
    });

    try {
      const standardRubricCriterias = _.get(
        standardRubric,
        "standardRubricCriterias",
        []
      );

      const _removeStandardRubricCriteriasResponse = {
        response: _.filter(standardRubricCriterias, criterion =>
          _.includes(ids, criterion.id)
        ),
        warning: null,
        isSuccess: true,
        __typename: "UpdateStandardRubricCriteriaResponse",
      };

      await client.mutate({
        mutation: removeStandardRubricCriteriasMutation,
        variables: { input: ids },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            removeStandardRubricCriterias: _removeStandardRubricCriteriasResponse,
            __typename: "PlannerMutations",
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { removeStandardRubricCriterias },
            },
          }
        ) => {
          if (removeStandardRubricCriterias?.isSuccess) {
            const updatedStandardRubricCriterias = _.filter(
              standardRubricCriterias,
              criteria => !_.includes(ids, criteria.id)
            );

            // Calculate total max score for associateScoreTool
            const totalMaxScore = _.reduce(
              updatedStandardRubricCriterias,
              (sum, criteria) => {
                return sum + (criteria.maxScore || 0);
              },
              0
            );

            // Update the associateScoreTool maxScore
            const updatedAssociateScoreTool = {
              ..._.get(standardRubric, "associateScoreTool", {}),
              scoreToolData: {
                ..._.get(
                  standardRubric,
                  "associateScoreTool.scoreToolData",
                  {}
                ),
                maxScore: totalMaxScore,
              },
            };

            const data = {
              ...standardRubric,
              standardRubricCriterias: updatedStandardRubricCriterias,
              associateScoreTool: _.has(
                standardRubric,
                "associateScoreTool.scoreToolData"
              )
                ? updatedAssociateScoreTool
                : null,
            };

            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: toolId,
                data,
                type,
              })
            );
          }
        },
      });
    } catch (e) {
      writeAssessmentToolNodeDetailsInCache({
        id: toolId,
        data: standardRubric,
        type,
      });
    }
  };
};

export const removeStandardRubricDescriptors = ({ toolId, ids, type }) => {
  return async () => {
    const standardRubric = getAssessmentToolNodeDetailsFromCacheV2({
      id: toolId,
      type,
    });

    try {
      const standardRubricCriterias = _.get(
        standardRubric,
        "standardRubricCriterias",
        []
      );

      // Find all descriptors that will be removed to construct the response
      const descriptorsToRemove = _.reduce(
        standardRubricCriterias,
        (result, criteria) => {
          const matchingDescriptors = _.filter(
            criteria.descriptors,
            descriptor => _.includes(ids, descriptor.id)
          );
          return [...result, ...matchingDescriptors];
        },
        []
      );

      const _removeStandardRubricDescriptorsResponse = {
        response: descriptorsToRemove,
        warning: null,
        isSuccess: true,
        __typename: "UpdateStandardRubricDescriptorResponse",
      };

      await client.mutate({
        mutation: removeStandardRubricDescriptorsMutation,
        variables: { input: ids },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            removeStandardRubricDescriptors: _removeStandardRubricDescriptorsResponse,
            __typename: "PlannerMutations",
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { removeStandardRubricDescriptors },
            },
          }
        ) => {
          if (removeStandardRubricDescriptors?.isSuccess) {
            // Remove the descriptors from each criteria and update maxScore
            const updatedCriterias = _.map(
              standardRubricCriterias,
              criteria => {
                // Remove descriptors that match the ids
                const remainingDescriptors = _.filter(
                  criteria.descriptors,
                  descriptor => !_.includes(ids, descriptor.id)
                );

                // Recalculate maxScore based on remaining descriptors
                const descriptorScores = _.map(remainingDescriptors, "score");

                const maxScore = _.max(descriptorScores) || null;

                return {
                  ...criteria,
                  descriptors: remainingDescriptors,
                  maxScore: maxScore,
                };
              }
            );

            // Calculate total max score for associateScoreTool
            const totalMaxScore = _.reduce(
              updatedCriterias,
              (sum, criteria) => {
                return sum + (criteria.maxScore || 0);
              },
              0
            );

            // Update the associateScoreTool maxScore
            const updatedAssociateScoreTool = {
              ..._.get(standardRubric, "associateScoreTool", {}),
              scoreToolData: {
                ..._.get(
                  standardRubric,
                  "associateScoreTool.scoreToolData",
                  {}
                ),
                maxScore: totalMaxScore,
              },
            };

            const data = {
              ...standardRubric,
              standardRubricCriterias: updatedCriterias,
              associateScoreTool: _.has(
                standardRubric,
                "associateScoreTool.scoreToolData"
              )
                ? updatedAssociateScoreTool
                : null,
            };

            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: toolId,
                data,
                type,
              })
            );
          }
        },
      });
    } catch (e) {
      writeAssessmentToolNodeDetailsInCache({
        id: toolId,
        data: standardRubric,
        type,
      });
    }
  };
};

export const updateStandardRubricCriterias = ({ input, toolId, type }) => {
  return async () => {
    const standardRubric = getAssessmentToolNodeDetailsFromCacheV2({
      id: toolId,
      type,
    });

    try {
      const standardRubricCriterias = _.get(
        standardRubric,
        "standardRubricCriterias",
        []
      );

      // Create a map to find original criteria
      const criteriaMap = _.keyBy(standardRubricCriterias, "id");

      // Create optimistic response based on input
      const _updateStandardRubricCriteriasResponse = {
        response: _.castArray(input).map(updateItem => {
          // Find the original criteria to merge with updates
          const originalCriteria = criteriaMap[updateItem.id] || {};

          return {
            ...originalCriteria,
            ...updateItem,
            __typename: "StandardRubricCriteria",
          };
        }),
        warning: null,
        isSuccess: true,
        __typename: "UpdateStandardRubricCriteriaResponse",
      };

      await client.mutate({
        mutation: updateStandardRubricCriteriasMutation,
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateStandardRubricCriterias: _updateStandardRubricCriteriasResponse,
            __typename: "PlannerMutations",
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { updateStandardRubricCriterias },
            },
          }
        ) => {
          if (updateStandardRubricCriterias?.isSuccess) {
            // Create a map of updated criteria by ID for easy lookup
            const updatedCriteriaMap = _.keyBy(
              updateStandardRubricCriterias.response,
              "id"
            );

            // Update each criteria with the corresponding updated values
            const updatedCriterias = _.map(
              standardRubricCriterias,
              criteria => {
                if (updatedCriteriaMap[criteria.id]) {
                  return {
                    ...criteria,
                    ...updatedCriteriaMap[criteria.id],
                  };
                }
                return criteria;
              }
            );

            // Calculate total max score for associateScoreTool
            const totalMaxScore = _.reduce(
              updatedCriterias,
              (sum, criteria) => {
                return sum + (criteria.maxScore || 0);
              },
              0
            );

            // Update the associateScoreTool maxScore
            const updatedAssociateScoreTool = {
              ..._.get(standardRubric, "associateScoreTool", {}),
              scoreToolData: {
                ..._.get(
                  standardRubric,
                  "associateScoreTool.scoreToolData",
                  {}
                ),
                maxScore: totalMaxScore,
              },
            };

            const data = {
              ...standardRubric,
              standardRubricCriterias: _.sortBy(
                updatedCriterias,
                "displaySequence"
              ),
              associateScoreTool: _.has(
                standardRubric,
                "associateScoreTool.scoreToolData"
              )
                ? updatedAssociateScoreTool
                : null,
            };

            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: toolId,
                data,
                type,
              })
            );
          }
        },
      });
    } catch (e) {
      writeAssessmentToolNodeDetailsInCache({
        id: toolId,
        data: standardRubric,
        type,
      });
    }
  };
};

export const updateStandardRubricDescriptors = ({ input, toolId, type }) => {
  return async () => {
    const standardRubric = getAssessmentToolNodeDetailsFromCacheV2({
      id: toolId,
      type,
    });

    try {
      const standardRubricCriterias = _.get(
        standardRubric,
        "standardRubricCriterias",
        []
      );

      // Create a map to find original descriptors using _.keyBy and flatMap
      const descriptorMap = _.keyBy(
        _.flatMap(
          standardRubricCriterias,
          criteria => criteria.descriptors || []
        ),
        "id"
      );

      const _updateStandardRubricDescriptorsResponse = {
        response: _.castArray(input)
          .filter(updateItem => descriptorMap[updateItem.id])
          .map(updateItem => {
            const originalDescriptor = descriptorMap[updateItem.id];
            return {
              ...originalDescriptor,
              ...updateItem,
              __typename: "StandardRubricDescriptor",
            };
          }),
        warning: null,
        isSuccess: true,
        __typename: "UpdateStandardRubricDescriptorResponse",
      };

      const response = await client.mutate({
        mutation: updateStandardRubricDescriptorsMutation,
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateStandardRubricDescriptors: _updateStandardRubricDescriptorsResponse,
            __typename: "PlannerMutations",
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { updateStandardRubricDescriptors },
            },
          }
        ) => {
          if (updateStandardRubricDescriptors?.isSuccess) {
            // Create a map of updated descriptors by ID for easy lookup
            const updatedDescriptorMap = _.keyBy(
              updateStandardRubricDescriptors.response,
              "id"
            );

            // Update each criteria's descriptors with the corresponding updated values
            const updatedCriterias = _.map(
              standardRubricCriterias,
              criteria => {
                const updatedCriteriaDescriptors = _.sortBy(
                  _.map(criteria.descriptors, descriptor => {
                    if (updatedDescriptorMap[descriptor.id]) {
                      return {
                        ...descriptor,
                        ...updatedDescriptorMap[descriptor.id],
                      };
                    }
                    return descriptor;
                  }),
                  "displaySequence"
                );

                return {
                  ...criteria,
                  descriptors: updatedCriteriaDescriptors,
                  maxScore:
                    _.max(_.map(updatedCriteriaDescriptors, "score")) || null,
                };
              }
            );

            // Calculate total max score for associateScoreTool
            const totalMaxScore = _.reduce(
              updatedCriterias,
              (sum, criteria) => {
                return sum + (criteria.maxScore || 0);
              },
              0
            );

            // Update the associateScoreTool maxScore
            const updatedAssociateScoreTool = {
              ..._.get(standardRubric, "associateScoreTool", {}),
              scoreToolData: {
                ..._.get(
                  standardRubric,
                  "associateScoreTool.scoreToolData",
                  {}
                ),
                maxScore: totalMaxScore,
              },
            };

            const data = {
              ...standardRubric,
              standardRubricCriterias: updatedCriterias,
              associateScoreTool: _.has(
                standardRubric,
                "associateScoreTool.scoreToolData"
              )
                ? updatedAssociateScoreTool
                : null,
            };

            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: toolId,
                data,
                type,
              })
            );
          }
        },
      });

      return _.get(
        response,
        "data.planner.updateStandardRubricDescriptors.response"
      );
    } catch (e) {
      writeAssessmentToolNodeDetailsInCache({
        id: toolId,
        data: standardRubric,
        type,
      });
    }
  };
};

export const updateScoreToolData = ({ input, toolId, type }) => {
  return async dispatch => {
    // Store original data before making any changes
    const originalStandardRubric = getAssessmentToolNodeDetailsFromCacheV2({
      id: toolId,
      type,
    });

    try {
      // Create optimistic response
      const _updateScoreToolDataResponse = {
        response: {
          id: input.id,
          isCustomMaxScore: true,
          __typename: "ScoreToolData",
        },
        warning: null,
        __typename: "ScoreToolDataResponse",
      };

      await client.mutate({
        mutation: updateScoreToolDataMutation,
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateScoreToolData: _updateScoreToolDataResponse,
            __typename: "PlannerMutations",
          },
        },
        update: () => {
          const updatedAssociateScoreTool = {
            ...originalStandardRubric.associateScoreTool,
            scoreToolData: {
              ..._.get(
                originalStandardRubric,
                "associateScoreTool.scoreToolData",
                {}
              ),
              maxScore: input.maxScore,
              __typename: "ScoreToolData",
            },

            __typename: "ScoreAssessmentTool",
          };

          // Create updated cache data
          const data = {
            ...originalStandardRubric,
            associateScoreTool: _.has(
              originalStandardRubric,
              "associateScoreTool.scoreToolData"
            )
              ? updatedAssociateScoreTool
              : null,
          };

          // Write to cache
          writeAssessmentToolNodeDetailsInCache({
            id: input.toolId,
            data,
            type,
          });
        },
      });
    } catch (e) {
      // show error toast
      let errorMessage = "toastMsgs:something_went_wrong";
      if (e.networkError) {
        errorMessage = "toastMsgs:no_internet_connection";
      }

      dispatch(setToastMsg(errorMessage));

      // re-write original cache data
      writeAssessmentToolNodeDetailsInCache({
        id: input.toolId,
        data: originalStandardRubric,
        type,
      });
    }
  };
};
